import { getCurrentApi, setSelectedHotel, getSelectedHotel } from './api';
import { User, LoginRequest, UserRole } from '../models/User';
import { getHotelById } from './hotelService';
import * as firebaseService from './firebaseService';
import { auditService } from './auditService';


export const login = async (credentials: LoginRequest): Promise<User> => {
  console.log("Login request initiated with credentials:", {
    username: credentials.username,
    hotelId: credentials.hotelId,
    hasPassword: !!credentials.password
  });
  
  // Check if we need to update the API URL based on the hotel ID
  if (credentials.hotelId) {
    try {
      // Use the admin API endpoint to get hotel details
      console.log("Fetching hotel details for ID:", credentials.hotelId);
      const hotel = await getHotelById(credentials.hotelId);
      console.log("Hotel details fetched:", {
        name: hotel.name,
        serverUrl: hotel.serverUrl,
        serverPort: hotel.serverPort
      });
      
      // Set the selected hotel which will update the API endpoint
      setSelectedHotel(hotel);
      
      // Update hotel status in Firebase
      try {
        const fbHotel = await firebaseService.getFirebaseHotelByBackendId(credentials.hotelId);
        
        if (fbHotel) {
          // Check hotel status before proceeding
          const status = await firebaseService.checkHotelStatus(fbHotel);
          if (status === 'offline') {
            throw new Error('This hotel is currently offline. Please try again later.');
          }
          
          // Update hotel status in Firebase
          if (fbHotel.id) {
            await firebaseService.updateHotelInFirebase(fbHotel.id, { 
              status: 'online', 
              lastChecked: new Date() 
            });
          }
        } else {
          // If hotel is not in Firebase, sync it
          const syncedHotel = await firebaseService.syncHotelWithFirebase(hotel);
          if (syncedHotel?.id) {
            await firebaseService.updateHotelInFirebase(syncedHotel.id, {
              status: 'online',
              lastChecked: new Date()
            });
          } else {
            // Check newly synced hotel status
            await firebaseService.checkHotelStatus(syncedHotel);
          }
        }
      } catch (statusError) {
        console.error('Error checking hotel status:', statusError);
        // If the error specifically mentions 'offline', propagate it
        if (statusError instanceof Error && statusError.message.includes('offline')) {
          throw statusError;
        }
        // Otherwise continue with login attempt even if status check fails
      }
    } catch (error) {
      console.error('Error loading hotel details:', error);
      // Rethrow if this is a status check error
      if (error instanceof Error && error.message.includes('offline')) {
        throw error;
      }
      // Continue with login using the current API endpoint for other errors
    }
  }
  
  
  console.log("Making API request to /auth/login");
  
  // Perform login with the API pointing to the right server
  try {
    const api = getCurrentApi();
    const response = await api.post<User>('/auth/login', credentials);
    
    console.log("Login successful, received user data:", {
      id: response.data.id,
      username: response.data.username,
      role: response.data.role,
      hotelId: response.data.hotelId,
      tokenLength: response.data.token.length
    });
    
    // Save authentication data
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data));
    
    // Log the login action to Firebase
    try {
      await auditService.logAction('User Login', {
        additionalInfo: `User ${response.data.username} logged in with role: ${response.data.role}`
      });
    } catch (error) {
      console.error('Error logging login action:', error);
      // Non-blocking error, continue with login flow
    }
    
    // Register with Firebase if hotel is in Firebase
    if (response.data.hotelId) {
      try {
        const fbHotel = await firebaseService.getFirebaseHotelByBackendId(response.data.hotelId);
        if (fbHotel?.id) {
          await registerUserWithFirebase(
            response.data.username, 
            fbHotel.id, 
            response.data.hotelId, 
            response.data.role
          );
        }
      } catch (err) {
        console.error("Failed to register user with Firebase", err);
        // Non-blocking error, continue with login flow
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Login API request failed:", error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  // Log the logout action before clearing the user data
  try {
    const user = getStoredUser();
    if (user) {
      await auditService.logAction('User Logout', {
        additionalInfo: `User ${user.username} logged out`
      });
    }
  } catch (error) {
    console.error('Error logging logout action:', error);
    // Non-blocking error, continue with logout
  }
  
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('selectedHotel');
};

export const getCurrentUser = async (): Promise<User> => {
  const api = getCurrentApi();
  const response = await api.get<User>('/users/me');
  localStorage.setItem('token', response.data.token);
  localStorage.setItem('user', JSON.stringify(response.data));
  return response.data;
};

export const isAuthenticated = (): boolean => {
  return localStorage.getItem('token') !== null;
};

export const getStoredUser = (): User | null => {
  const user = localStorage.getItem('user');
  if (user) {
    return JSON.parse(user);
  }
  return null;
};

/**
 * Register user with Firebase
 * This is used to keep track of which users belong to which hotels
 */
export const registerUserWithFirebase = async (
  username: string, 
  firebaseHotelId: string, 
  backendHotelId: number, 
  role: UserRole
): Promise<void> => {
  try {
    await firebaseService.addUserToFirebase(username, firebaseHotelId, backendHotelId, role);
    console.log(`User ${username} registered with Firebase for hotel ${firebaseHotelId}`);
  } catch (error) {
    console.error('Error registering user with Firebase:', error);
    // Continue even if Firebase registration fails
  }
};